/**
 * Booking-checklist logic — pure port of renderBL()/blToggle()/onAct()/onSt()
 * (reference.html lines 1848–1984), with the reference's in-place mutations of
 * blChecked surfaced as explicit `impliedChecks` so they can be synced.
 */
import type { BLDef, Checks, Slots, SlotValue, Status, TripDefinition } from "../types.js";
import { defDur } from "./schedule.js";

export const STATUS_LABELS = ["○ Deciding", "✔ Confirmed", "🔒 Booked"] as const;

export function bookable(def: TripDefinition, actId: string): boolean {
  return !!def.act[actId] && def.act[actId].badge === "book";
}

/** actId → max status across all slots using it */
export function actStatuses(slots: Slots): Record<string, Status> {
  const st: Record<string, Status> = {};
  for (const v of Object.values(slots)) st[v.act] = Math.max(st[v.act] || 0, v.st) as Status;
  return st;
}

/** Where/when a bookable activity actually sits in the itinerary. */
export interface Placement {
  /** "Thu Sep 17" */
  dayLabel: string;
  /** computed start, e.g. "10:00 AM" */
  timeLabel: string;
}

export interface ChecklistItem extends BLDef {
  checked: boolean;
  /** checked (booked) but its activity is no longer 🔒 in the itinerary */
  orphan: boolean;
  /** the date/time this reservation needs to be for (null for `always` items) */
  scheduled: Placement | null;
}

export interface ChecklistComputation {
  items: ChecklistItem[];
  /**
   * Checks the reference would have flipped on during render (curated items whose
   * trigger act reached 🔒, and existing auto_ keys whose act reached 🔒).
   * Callers should persist these so both browsers agree.
   */
  impliedChecks: Checks;
}

const autoEntry = (def: TripDefinition, id: string, k: string): BLDef => ({
  k,
  req: [id],
  when: "BOOK AHEAD",
  cls: "soon",
  what: def.act[id].name,
  why: def.act[id].book || "",
  url: def.act[id].web || "",
  link: "Book",
});

export function computeChecklist(
  def: TripDefinition,
  slots: Slots,
  checks: Checks,
  /** actId → when it's scheduled; supplied by the caller from computeTrip */
  placements: Record<string, Placement> = {}
): ChecklistComputation {
  const stOf = actStatuses(slots);
  const items: BLDef[] = def.bl.filter(
    (b) => b.always || (b.req && (b.req.some((id) => (stOf[id] || 0) >= 1) || checks[b.k]))
  );
  const covered = new Set<string>();
  for (const b of def.bl) for (const id of b.req || []) covered.add(id);

  /* auto-generate entries for confirmed bookable acts without a curated entry */
  for (const [id, st] of Object.entries(stOf)) {
    if (st >= 1 && def.act[id] && def.act[id].badge === "book" && !covered.has(id))
      items.push(autoEntry(def, id, "auto_" + id));
  }

  /* Booked ⇒ checked (the reference mutates blChecked here; we report it) */
  const impliedChecks: Checks = {};
  const effective: Checks = { ...checks };
  for (const b of def.bl) {
    if (b.req && b.req.some((id) => (stOf[id] || 0) === 2) && !effective[b.k]) {
      impliedChecks[b.k] = true;
      effective[b.k] = true;
    }
  }
  for (const k of Object.keys(checks)) {
    if (k.startsWith("auto_") && (stOf[k.slice(5)] || 0) === 2 && !effective[k]) {
      impliedChecks[k] = true;
      effective[k] = true;
    }
  }

  /* checked auto_ entries whose act is gone from BL coverage keep a row (orphans) */
  for (const k of Object.keys(effective)) {
    if (k.startsWith("auto_") && effective[k]) {
      const id = k.slice(5);
      if (def.act[id] && !covered.has(id) && !items.some((b) => b.k === k)) items.push(autoEntry(def, id, k));
    }
  }

  return {
    items: items.map((b) => ({
      ...b,
      checked: !!effective[b.k],
      orphan: !!(effective[b.k] && b.req && !b.req.some((id) => (stOf[id] || 0) === 2)),
      scheduled: (b.req || []).map((id) => placements[id]).find(Boolean) ?? null,
    })),
    impliedChecks,
  };
}

export interface StateChange {
  kind: "slot" | "check";
  key: string;
  value: SlotValue | { on: boolean };
}

/**
 * Ticking/unticking a checklist row (blToggle): the check itself plus every
 * slot whose act is in req and already Confirmed+ set to Booked (or back).
 * Returned as one batch so observers never see half the cascade.
 */
export function checklistToggleCascade(
  def: TripDefinition,
  slots: Slots,
  k: string,
  on: boolean
): StateChange[] {
  const changes: StateChange[] = [{ kind: "check", key: k, value: { on } }];
  const b = def.bl.find((x) => x.k === k) || (k.startsWith("auto_") ? { req: [k.slice(5)] } : null);
  if (b && b.req) {
    for (const [sk, v] of Object.entries(slots)) {
      if (b.req.includes(v.act) && v.st >= 1) {
        changes.push({ kind: "slot", key: sk, value: { ...v, st: on ? 2 : 1 } });
      }
    }
  }
  return changes;
}

/** ○ → ✔ → 🔒 → ○ (walk-up/free acts have no 🔒 state) */
export function cycleStatus(def: TripDefinition, slot: SlotValue): SlotValue {
  return { ...slot, st: ((slot.st + 1) % (bookable(def, slot.act) ? 3 : 2)) as Status };
}

/** Switching a slot's activity: keep 🔒 only if the new act is bookable (onAct). */
export function applyActChange(def: TripDefinition, prev: SlotValue, actId: string): SlotValue {
  const keep =
    prev.st === 2 && def.act[actId].badge === "book"
      ? 2
      : Math.min(prev.st, def.act[actId].badge === "book" ? 2 : 1);
  return { act: actId, dur: defDur(def, actId), st: keep as Status };
}
